import { useEffect, useRef, useState } from 'react'

const THRESHOLD = 64   // px of pull needed to trigger
const MAX_PULL  = 80   // max visual travel distance

export function usePullToRefresh(onRefresh: () => Promise<void>) {
  const [pullDistance, setPullDistance] = useState(0)
  const [refreshing, setRefreshing]     = useState(false)

  const startY       = useRef<number | null>(null)
  const dist         = useRef(0)
  const isRefreshing = useRef(false)
  const onRefreshRef = useRef(onRefresh)
  onRefreshRef.current = onRefresh

  useEffect(() => {
    async function trigger() {
      isRefreshing.current = true
      setRefreshing(true)
      try {
        await onRefreshRef.current()
      } finally {
        isRefreshing.current = false
        setRefreshing(false)
        setPullDistance(0)
        dist.current = 0
      }
    }
    const onTouchStart = (e: TouchEvent) => {
      if (window.scrollY > 0 || isRefreshing.current) return
      startY.current = e.touches[0].clientY
    }

    const onTouchMove = (e: TouchEvent) => {
      if (startY.current === null || isRefreshing.current) return
      const dy = e.touches[0].clientY - startY.current
      if (dy <= 0) {
        dist.current = 0
        setPullDistance(0)
        return
      }
      // Rubber-band resistance
      const d = Math.min(dy * 0.45, MAX_PULL)
      dist.current = d
      setPullDistance(d)
      if (dy > 8) e.preventDefault()
    }

    const onTouchEnd = () => {
      if (startY.current === null) return
      startY.current = null
      if (dist.current >= THRESHOLD * 0.6) {
        trigger()
      } else {
        setPullDistance(0)
        dist.current = 0
      }
    }

    document.addEventListener('touchstart', onTouchStart, { passive: true })
    document.addEventListener('touchmove',  onTouchMove,  { passive: false })
    document.addEventListener('touchend',   onTouchEnd,   { passive: true })
    document.addEventListener('touchcancel',onTouchEnd,   { passive: true })

    return () => {
      document.removeEventListener('touchstart', onTouchStart)
      document.removeEventListener('touchmove',  onTouchMove)
      document.removeEventListener('touchend',   onTouchEnd)
      document.removeEventListener('touchcancel',onTouchEnd)
    }
  }, [])

  return { pullDistance, refreshing }
}
